import { useEffect, useState } from "react";
import { Redirect } from "wouter";
import { getMerchantToken, merchantFetch } from "../lib/merchantAuth";
import { MerchantNav } from "../components/MerchantNav";

/**
 * Merchant Streak Settings page — Phase 20.
 *
 * Single column, three sections:
 *   • Config — streak enable/cadence radio + per-kind milestone toggles
 *     (visits / tenure / spend). Saves via PATCH /tenant/streaks/config.
 *     Cadence change resets every customer's streak (D77) — surfaced
 *     with an inline confirmation banner before the patch fires.
 *   • Leaderboard — top 20 active streaks (current desc, last-period
 *     desc as tiebreak). Customers with display name + masked phone.
 *   • Analytics — date range picker (defaults to last 30 days) +
 *     aggregate counters (active streak count, longest active,
 *     average current, milestones-fired-in-window grouped by kind).
 *
 * No charts — the spec said "aggregate counts + averages" and the
 * cashier doesn't need a sparkline to see whether the engagement
 * loop is working. Plain numbers + a simple bar for milestone
 * counts is enough for v1.
 *
 * Mirrors MerchantSurprisePool.tsx for the auth + fetch pattern
 * (merchantFetch instead of the api-client-react hooks, since
 * TanStack Query is wired to the customer-side auth provider).
 */

type Cadence = "DAILY" | "WEEKLY" | "BIWEEKLY" | "MONTHLY";

interface ConfigWire {
  tenantId: string;
  streakEnabled: boolean;
  streakCadence: Cadence;
  visitMilestonesEnabled: boolean;
  tenureMilestonesEnabled: boolean;
  spendMilestonesEnabled: boolean;
  updatedAt: string;
}

interface LeaderboardEntry {
  customerId: string;
  customerDisplayName: string;
  customerPhoneLast4: string;
  currentStreakCount: number;
  longestStreakCount: number;
  lastVisitAt: string | null;
}

type MilestoneKind = "VISIT_COUNT" | "TENURE_DAYS" | "SPEND_CENTAVOS";

interface AnalyticsWire {
  fromIso: string;
  toIso: string;
  totalMilestonesFired: number;
  milestonesByKind: Record<MilestoneKind, number>;
  customersWithActiveStreaks: number;
  averageCurrentStreakLength: number;
}

const CADENCE_LABELS: Record<Cadence, string> = {
  DAILY: "Daily",
  WEEKLY: "Weekly (Mon–Sun, Asia/Manila)",
  BIWEEKLY: "Every 2 weeks",
  MONTHLY: "Monthly",
};

function todayISODate(d: Date = new Date()): string {
  // ISO yyyy-mm-dd in UTC — the analytics endpoint accepts either
  // a date or a full ISO timestamp; date is cleaner in the URL.
  return d.toISOString().slice(0, 10);
}

function daysAgoISODate(n: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - n);
  return todayISODate(d);
}

function formatPeriodStart(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("en-PH", {
      month: "short",
      day: "numeric",
      timeZone: "Asia/Manila",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export function MerchantStreakSettingsPage() {
  const token = getMerchantToken();
  if (!token) return <Redirect to="/merchant/login" />;

  const [config, setConfig] = useState<ConfigWire | null>(null);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[] | null>(
    null,
  );
  const [analytics, setAnalytics] = useState<AnalyticsWire | null>(null);
  const [from, setFrom] = useState<string>(daysAgoISODate(30));
  const [to, setTo] = useState<string>(todayISODate());
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [saveErr, setSaveErr] = useState<string | null>(null);
  const [saveOk, setSaveOk] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [pendingCadence, setPendingCadence] = useState<Cadence | null>(null);

  // Initial config + leaderboard load
  useEffect(() => {
    let cancel = false;
    (async () => {
      const [cr, lr] = await Promise.all([
        merchantFetch<ConfigWire>("/api/v1/tenant/streaks/config", {
          method: "GET",
        }),
        merchantFetch<{ entries: LeaderboardEntry[] }>(
          "/api/v1/tenant/streaks/leaderboard?limit=20",
          { method: "GET" },
        ),
      ]);
      if (cancel) return;
      if (cr.ok) setConfig(cr.data);
      else setLoadErr(cr.message);
      if (lr.ok) setLeaderboard(lr.data.entries);
    })();
    return () => {
      cancel = true;
    };
  }, []);

  // Analytics reloads when from/to change
  useEffect(() => {
    let cancel = false;
    (async () => {
      const r = await merchantFetch<AnalyticsWire>(
        `/api/v1/tenant/streaks/analytics?from=${encodeURIComponent(
          from,
        )}&to=${encodeURIComponent(to)}`,
        { method: "GET" },
      );
      if (cancel) return;
      if (r.ok) setAnalytics(r.data);
    })();
    return () => {
      cancel = true;
    };
  }, [from, to]);

  async function persist(patch: Partial<ConfigWire>): Promise<void> {
    if (!config) return;
    setSaving(true);
    setSaveErr(null);
    setSaveOk(null);
    const r = await merchantFetch<ConfigWire>("/api/v1/tenant/streaks/config", {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
    setSaving(false);
    if (r.ok) {
      setConfig(r.data);
      setSaveOk("Saved.");
      window.setTimeout(() => setSaveOk(null), 2500);
      // A cadence change wipes every customer's streak (D77) — refresh
      // the leaderboard so we don't show stale counts.
      if (patch.streakCadence) {
        const lr = await merchantFetch<{ entries: LeaderboardEntry[] }>(
          "/api/v1/tenant/streaks/leaderboard?limit=20",
          { method: "GET" },
        );
        if (lr.ok) setLeaderboard(lr.data.entries);
      }
    } else {
      setSaveErr(r.message);
    }
  }

  function onCadenceClick(c: Cadence): void {
    if (!config) return;
    if (config.streakCadence === c) return;
    // First click → confirmation banner; second click on same value →
    // commit. This avoids accidentally nuking a multi-month leaderboard
    // because someone fat-fingered a radio button.
    if (pendingCadence !== c) {
      setPendingCadence(c);
      return;
    }
    setPendingCadence(null);
    void persist({ streakCadence: c });
  }

  if (loadErr) {
    return (
      <div className="mx-auto max-w-3xl p-4">
        <MerchantNav current="streaks" />
        <div className="mt-6 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          Could not load streak settings: {loadErr}
        </div>
      </div>
    );
  }
  if (!config) {
    return (
      <div className="mx-auto max-w-3xl p-4">
        <MerchantNav current="streaks" />
        <p className="mt-6 text-sm text-ink-dark">Loading…</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl p-4 pb-20">
      <MerchantNav current="streaks" />
      <h1 className="mt-6 text-xl font-semibold text-navy">
        Streaks &amp; Milestones
      </h1>
      <p className="mt-1 text-sm text-ink-dark">
        Reward repeat visits with a streak counter and celebrate visit /
        tenure / spend milestones. Customers see a 🔥 badge on their card
        and an amber ⏰ warning when their streak is about to lapse.
      </p>

      {saveErr ? (
        <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {saveErr}
        </div>
      ) : null}
      {saveOk ? (
        <div className="mt-4 rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-800">
          {saveOk}
        </div>
      ) : null}

      {/* CONFIG */}
      <section className="mt-6 rounded-lg border border-amber-light bg-white p-4">
        <h2 className="text-base font-semibold text-navy">Streak engine</h2>
        <label className="mt-3 flex items-center gap-3">
          <input
            type="checkbox"
            checked={config.streakEnabled}
            disabled={saving}
            onChange={(e) =>
              void persist({ streakEnabled: e.currentTarget.checked })
            }
          />
          <span className="text-sm text-navy">
            Track streaks for this store
          </span>
        </label>

        <fieldset
          className="mt-4"
          disabled={!config.streakEnabled || saving}
        >
          <legend className="text-sm font-semibold text-navy">Cadence</legend>
          <p className="mt-1 text-xs text-ink-dark">
            Changing the cadence resets every customer's current streak count
            to zero. Their longest-streak record is preserved.
          </p>
          <div className="mt-2 flex flex-col gap-2">
            {(Object.keys(CADENCE_LABELS) as Cadence[]).map((c) => {
              const isCurrent = config.streakCadence === c;
              const isPending = pendingCadence === c;
              return (
                <label key={c} className="flex items-start gap-2">
                  <input
                    type="radio"
                    name="cadence"
                    checked={isCurrent}
                    onChange={() => onCadenceClick(c)}
                    onClick={() => onCadenceClick(c)}
                  />
                  <span className="text-sm text-navy">
                    {CADENCE_LABELS[c]}
                    {isPending ? (
                      <span className="ml-2 text-amber-700">
                        Click again to confirm reset
                      </span>
                    ) : null}
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>

        <fieldset className="mt-5" disabled={saving}>
          <legend className="text-sm font-semibold text-navy">
            Milestones
          </legend>
          <div className="mt-2 flex flex-col gap-2 text-sm text-navy">
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={config.visitMilestonesEnabled}
                onChange={(e) =>
                  void persist({
                    visitMilestonesEnabled: e.currentTarget.checked,
                  })
                }
              />
              Visit count (5 / 10 / 25 / 50 / 100 / 250 / 500 / 1000)
            </label>
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={config.tenureMilestonesEnabled}
                onChange={(e) =>
                  void persist({
                    tenureMilestonesEnabled: e.currentTarget.checked,
                  })
                }
              />
              Tenure (30 / 90 / 180 / 365 days since first visit)
            </label>
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={config.spendMilestonesEnabled}
                onChange={(e) =>
                  void persist({
                    spendMilestonesEnabled: e.currentTarget.checked,
                  })
                }
              />
              Spend (₱500 / ₱2,500 / ₱10,000 / ₱25,000 / ₱50,000)
            </label>
          </div>
        </fieldset>
      </section>

      {/* LEADERBOARD */}
      <section className="mt-6 rounded-lg border border-amber-light bg-white p-4">
        <h2 className="text-base font-semibold text-navy">
          Top streaks (current)
        </h2>
        {leaderboard === null ? (
          <p className="mt-2 text-sm text-ink-dark">Loading…</p>
        ) : leaderboard.length === 0 ? (
          <p className="mt-2 text-sm text-ink-dark">
            No active streaks yet. They'll appear here once customers start
            scanning.
          </p>
        ) : (
          <ol className="mt-3 divide-y divide-amber-light">
            {leaderboard.map((row, i) => (
              <li
                key={row.customerId}
                className="flex items-center justify-between gap-3 py-2 text-sm"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <span className="w-6 shrink-0 text-right font-mono text-ink-dark">
                    {i + 1}.
                  </span>
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-navy">
                      {row.customerDisplayName || "Customer"}
                    </p>
                    <p className="text-xs text-ink-dark">
                      ••••{row.customerPhoneLast4} · last:{" "}
                      {formatPeriodStart(row.lastVisitAt)}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-base font-bold text-orange-600">
                    🔥 {row.currentStreakCount}
                  </p>
                  <p className="text-[11px] text-ink-dark">
                    best {row.longestStreakCount}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        )}
      </section>

      {/* ANALYTICS */}
      <section className="mt-6 rounded-lg border border-amber-light bg-white p-4">
        <h2 className="text-base font-semibold text-navy">Analytics</h2>
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <label className="flex flex-col text-xs text-ink-dark">
            From
            <input
              type="date"
              value={from}
              max={to}
              onChange={(e) => setFrom(e.currentTarget.value)}
              className="mt-1 rounded border border-amber-light px-2 py-1 text-sm"
            />
          </label>
          <label className="flex flex-col text-xs text-ink-dark">
            To
            <input
              type="date"
              value={to}
              min={from}
              max={todayISODate()}
              onChange={(e) => setTo(e.currentTarget.value)}
              className="mt-1 rounded border border-amber-light px-2 py-1 text-sm"
            />
          </label>
        </div>
        {analytics === null ? (
          <p className="mt-3 text-sm text-ink-dark">Loading…</p>
        ) : (
          <>
            <dl className="mt-4 grid grid-cols-3 gap-3 text-center">
              <div className="rounded bg-amber-xlight p-3">
                <dt className="text-[11px] uppercase text-ink-dark">
                  Active streaks
                </dt>
                <dd className="mt-1 text-2xl font-bold text-navy">
                  {analytics.customersWithActiveStreaks}
                </dd>
              </div>
              <div className="rounded bg-amber-xlight p-3">
                <dt className="text-[11px] uppercase text-ink-dark">
                  Avg. current
                </dt>
                <dd className="mt-1 text-2xl font-bold text-navy">
                  {analytics.averageCurrentStreakLength.toFixed(1)}
                </dd>
              </div>
              <div className="rounded bg-amber-xlight p-3">
                <dt className="text-[11px] uppercase text-ink-dark">
                  Milestones fired
                </dt>
                <dd className="mt-1 text-2xl font-bold text-navy">
                  {analytics.totalMilestonesFired}
                </dd>
              </div>
            </dl>
            <h3 className="mt-5 text-sm font-semibold text-navy">
              Milestones fired in window
            </h3>
            {analytics.totalMilestonesFired === 0 ? (
              <p className="mt-2 text-sm text-ink-dark">
                No milestones fired in this date range.
              </p>
            ) : (
              <ul className="mt-2 space-y-1 text-sm text-navy">
                {(Object.keys(analytics.milestonesByKind) as MilestoneKind[])
                  .filter((k) => analytics.milestonesByKind[k] > 0)
                  .map((k) => (
                    <li
                      key={k}
                      className="flex items-center justify-between gap-3"
                    >
                      <span>{milestoneLabel(k)}</span>
                      <span className="font-semibold">
                        {analytics.milestonesByKind[k]}
                      </span>
                    </li>
                  ))}
              </ul>
            )}
          </>
        )}
      </section>
    </div>
  );
}

function milestoneLabel(k: MilestoneKind): string {
  switch (k) {
    case "VISIT_COUNT":
      return "Visit milestones";
    case "TENURE_DAYS":
      return "Tenure milestones";
    case "SPEND_CENTAVOS":
      return "Spend milestones";
    default:
      return k;
  }
}
